// ─────────────────────────────────────────────
// KIBORG API Client
//
// Simple typed fetch wrapper for /api/v1 endpoints.
// Will be upgraded to Hono RPC typed client once API routes are built.
// Vite proxies /api → http://localhost:3000 to avoid CORS.
// ─────────────────────────────────────────────

const BASE = "/api/v1";

class ApiError extends Error {
  status: number;
  code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

// Paths where a 401 should NOT trigger an auto-redirect to /login
// (public auth pages + vendor/external portal routes that have their own flows).
const PUBLIC_PATH_PREFIXES = [
  "/login",
  "/forgot-password",
  "/reset-password",
  "/setup",
  "/vendor-portal",
  "/external",
];

let redirectingToLogin = false;

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function handleUnauthorized(): void {
  if (typeof window === "undefined" || redirectingToLogin) return;
  if (isPublicPath(window.location.pathname)) return;

  redirectingToLogin = true;
  // Clear persisted auth state so RequireAuth re-validates after login.
  try {
    window.localStorage.removeItem("kiborg-auth");
  } catch {
    // ignore storage errors
  }
  const from = window.location.pathname + window.location.search;
  window.location.replace(`/login?from=${encodeURIComponent(from)}`);
}

async function parseResponse<T>(res: Response): Promise<T> {
  const text = await res.text();

  // S-089: a successful empty body (204 No Content, DELETEs) is not a parse
  // error — callers get undefined instead of a fake PARSE_ERROR payload.
  if (res.ok && (res.status === 204 || text === "")) {
    return undefined as T;
  }

  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    // Non-JSON success bodies pass through as-is; error paths keep the
    // PARSE_ERROR envelope so the throw below has a code/message shape.
    if (res.ok) return text as T;
    data = { error: { code: "PARSE_ERROR", message: text } };
  }

  if (!res.ok) {
    if (res.status === 401) {
      handleUnauthorized();
    }
    const err = (data as { error?: { code?: string; message?: string } })?.error;
    throw new ApiError(
      res.status,
      err?.code ?? "UNKNOWN_ERROR",
      err?.message ?? `HTTP ${res.status}`
    );
  }

  return data as T;
}

function buildHeaders(body?: unknown, extra?: Record<string, string>): HeadersInit {
  const headers: Record<string, string> = { ...extra };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
}

export const api = {
  get: async <T = unknown>(path: string, extra?: Record<string, string>): Promise<T> => {
    const res = await fetch(`${BASE}${path}`, {
      credentials: "include",
      headers: buildHeaders(undefined, extra),
    });
    return parseResponse<T>(res);
  },

  post: async <T = unknown>(path: string, body?: unknown, extra?: Record<string, string>): Promise<T> => {
    const res = await fetch(`${BASE}${path}`, {
      method: "POST",
      credentials: "include",
      headers: buildHeaders(body, extra),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return parseResponse<T>(res);
  },

  put: async <T = unknown>(path: string, body?: unknown, extra?: Record<string, string>): Promise<T> => {
    const res = await fetch(`${BASE}${path}`, {
      method: "PUT",
      credentials: "include",
      headers: buildHeaders(body, extra),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return parseResponse<T>(res);
  },

  patch: async <T = unknown>(path: string, body?: unknown, extra?: Record<string, string>): Promise<T> => {
    const res = await fetch(`${BASE}${path}`, {
      method: "PATCH",
      credentials: "include",
      headers: buildHeaders(body, extra),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    return parseResponse<T>(res);
  },

  delete: async <T = unknown>(path: string, extra?: Record<string, string>): Promise<T> => {
    const res = await fetch(`${BASE}${path}`, {
      method: "DELETE",
      credentials: "include",
      headers: buildHeaders(undefined, extra),
    });
    return parseResponse<T>(res);
  },
};

export { ApiError };
